/**
 * Dossier screen — /app/brands/:brandCacheId. Full detail for one brand:
 * evidence strip, catalog products table (banded, no raw BSR/review
 * numbers — see bands.ts header), and the supplier workflow (find
 * candidates → promote → draft email → mark as contacted).
 *
 * Working tool, not a showcase: one accent action per section, restraint
 * everywhere else. Mirrors FeedPage's card conventions.
 */
import { useEffect, useReducer, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ClaimResponse, DossierProduct, SupplierCandidate } from '@sourcito/schemas';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../design';
import {
  handlingRiskLabel,
  amazonListingUrl,
  externalUrl,
  freshness,
  price,
  salesBand,
  sellerBand,
  sizeLabel,
} from '../../lib/bands';
import { ApiCallError } from '../../lib/api';
import {
  useAdvanceClaim,
  useClaimBrand,
  useDiscoverSupplier,
  useDossier,
  usePassBrand,
} from '../../lib/feed';
import {
  discoveryRunsQuery,
  useCandidates,
  useInvalidateDiscoveryRuns,
  usePromoteCandidate,
} from '../../lib/discovery';
import {
  initialOutreachComposerState,
  outreachComposerReducer,
  useDraftOutreach,
  useSendOutreach,
} from '../../lib/outreach';
import { AppShell } from '../../components/AppShell';
import { Skeleton } from '../../components/Skeleton';
import { CLAIM_STATUS_LABEL } from './FeedPage';
import { PassDialog } from './PassDialog';
import { OutreachComposer } from './OutreachComposer';

export function DossierPage() {
  const { brandCacheId } = useParams({ from: '/app/brands/$brandCacheId' });
  const { data: dossier, isLoading, isError, error } = useDossier(brandCacheId);

  const notFound = isError && error instanceof ApiCallError && error.status === 404;

  return (
    <AppShell>
      <main className="max-w-3xl mx-auto px-8 pb-8 pt-4 space-y-6">
        <Link
          to="/app/brands"
          className="text-sm text-fg-tertiary hover:text-fg-secondary hover:underline"
        >
          ← Back to Brands
        </Link>

        {isLoading && (
          <div className="space-y-4" aria-hidden="true">
            <Card>
              <Skeleton className="h-5 w-1/3 rounded-xs" />
              <Skeleton className="h-3 w-2/3 rounded-xs mt-3" />
            </Card>
            <Card>
              <Skeleton className="h-3 w-full rounded-xs" />
              <Skeleton className="h-3 w-5/6 rounded-xs mt-2" />
            </Card>
          </div>
        )}

        {notFound && (
          <Card className="text-center py-14">
            <h2 className="text-base font-medium mb-2">Brand not found</h2>
            <p className="text-sm text-fg-secondary">
              This brand isn't in the catalog, or the link is out of date.
            </p>
          </Card>
        )}

        {isError && !notFound && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm font-medium text-negative">Failed to load this brand</p>
            <p className="text-sm text-fg-secondary mt-1">
              {(error as Error)?.message ?? 'Unknown error'}
            </p>
          </Card>
        )}

        {dossier && <DossierContent brandCacheId={brandCacheId} dossier={dossier} />}
      </main>
    </AppShell>
  );
}

function DossierContent({
  brandCacheId,
  dossier,
}: {
  brandCacheId: string;
  dossier: NonNullable<ReturnType<typeof useDossier>['data']>;
}) {
  const claimBrand = useClaimBrand();
  const passBrand = usePassBrand();
  const navigate = useNavigate();
  const [claimId, setClaimId] = useState<string | null>(null);
  const [passOpen, setPassOpen] = useState(false);

  // The dossier only carries claimStatus, not the claim's id (PATCH
  // /api/claims/:id needs the id). When the brand is already claimed from a
  // prior visit we don't have that id in local state yet — POST /api/claims
  // is idempotent (get-or-create, no duplicate audit row, see
  // routes/claims.ts) so resolving it this way is a safe no-op rather than
  // a real re-claim. No separate GET-claim-by-brand endpoint exists yet.
  useEffect(() => {
    if (dossier.claimStatus != null && !claimId && !claimBrand.isPending) {
      claimBrand.mutate(
        { brandCacheId },
        { onSuccess: (res: ClaimResponse) => setClaimId(res.id) }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossier.claimStatus, claimId, brandCacheId]);

  const amazonHeavy = dossier.amazonShare != null && dossier.amazonShare > 0.3;

  return (
    <>
      <header>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-medium leading-tight">{dossier.displayName}</h1>
          <Badge variant="neutral">{sizeLabel(dossier.sizeBucket, dossier.catalogSize)}</Badge>
          {dossier.claimStatus != null && (
            <Badge variant={dossier.claimStatus === 'dropped' ? 'neutral' : 'accent'}>
              {CLAIM_STATUS_LABEL[dossier.claimStatus]}
            </Badge>
          )}
          {(dossier.independentSellers ?? 0) >= 1 && !dossier.brandDirectOnly && (
            <Badge variant="positive">Sells via independent resellers</Badge>
          )}
          {dossier.brandDirectOnly === true && (
            <Badge variant="warning">Brand sells it themselves</Badge>
          )}
          {dossier.cpgParent != null && (
            <Badge variant="warning">Owned by {dossier.cpgParent}</Badge>
          )}
          {(dossier.handlingRisks ?? []).map((r) => (
            <Badge key={r} variant="warning">
              {handlingRiskLabel(r)}
            </Badge>
          ))}
        </div>
        {dossier.claimStatus == null && (
          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              onClick={() =>
                claimBrand.mutate(
                  { brandCacheId },
                  { onSuccess: (res: ClaimResponse) => setClaimId(res.id) }
                )
              }
              loading={claimBrand.isPending}
            >
              Claim
            </Button>
            <Button
              size="sm"
              variant="secondary"
              loading={passBrand.isPending}
              onClick={() => setPassOpen(true)}
            >
              Pass
            </Button>
            <PassDialog
              brandName={dossier.displayName}
              open={passOpen}
              passing={passBrand.isPending}
              onConfirm={(reason) =>
                // Passed brands leave the feed — return there rather than
                // stranding the user on a rejected brand's dossier.
                passBrand
                  .mutateAsync({ name: dossier.displayName, reason: reason || undefined })
                  .then(() => navigate({ to: '/app/brands' }))
                  .catch(() => {})
              }
              onClose={() => setPassOpen(false)}
            />
          </div>
        )}
        {passBrand.isError && (
          <p role="alert" className="text-xs text-negative mt-2">
            {(passBrand.error as Error)?.message ?? 'Pass failed'}
          </p>
        )}
        {claimBrand.isError && (
          <p role="alert" className="text-xs text-negative mt-2">
            {(claimBrand.error as Error)?.message ?? 'Claim failed'}
          </p>
        )}
      </header>

      <Card>
        <p className="text-sm text-fg-secondary">
          {salesBand(dossier.velocitySum)} ·{' '}
          {sellerBand(dossier.offerCountAvg != null ? Math.round(dossier.offerCountAvg) : null)} ·{' '}
          {dossier.qualifyingCount} products qualify · {freshness(dossier.freshnessAt)}
        </p>
        {amazonHeavy && (
          <p className="text-xs mt-1.5 text-warning">
            <span aria-hidden="true">⚠</span> Amazon present on many listings — check before
            committing
          </p>
        )}
      </Card>

      <ProductsTable products={dossier.products} />

      <SupplierSection
        brandCacheId={brandCacheId}
        topAsin={dossier.products[0]?.asin ?? null}
        claimId={claimId}
        claimStatus={dossier.claimStatus}
      />
    </>
  );
}

function ProductsTable({ products }: { products: DossierProduct[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Products</CardTitle>
        <CardDescription>{products.length} catalog listings for this brand.</CardDescription>
      </CardHeader>
      {products.length === 0 ? (
        <p className="text-sm text-fg-secondary">No catalog products yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-fg-tertiary border-b border-border-subtle">
                <th className="py-2 pr-3 font-medium">Title</th>
                <th className="py-2 pr-3 font-medium">Price</th>
                <th className="py-2 pr-3 font-medium">Sales</th>
                <th className="py-2 pr-3 font-medium">Sellers</th>
                <th className="py-2 font-medium">Amazon</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.asin} className="border-b border-border-subtle last:border-0">
                  <td className="py-2 pr-3 max-w-xs truncate" title={p.title ?? undefined}>
                    <a
                      href={amazonListingUrl(p.asin)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent-fg hover:underline"
                    >
                      {p.title ?? '(untitled)'}
                    </a>
                  </td>
                  <td className="py-2 pr-3 tabular-nums">{price(p.priceCents)}</td>
                  <td className="py-2 pr-3">{salesBand(p.monthlySold)}</td>
                  <td className="py-2 pr-3">{sellerBand(p.offerCountNew)}</td>
                  <td className="py-2">
                    {p.amazonOnListing ? (
                      <span className="text-warning" title="Amazon sells this listing">
                        <span aria-hidden="true">⚠</span>
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function SupplierSection({
  brandCacheId,
  topAsin,
  claimId,
  claimStatus,
}: {
  brandCacheId: string;
  topAsin: string | null;
  claimId: string | null;
  claimStatus: ClaimResponse['status'] | null;
}) {
  // supplier_candidates is per-ASIN, not per-brand. The dossier discovery
  // kickoff (POST /api/feed/:id/discover-supplier) runs against the brand's
  // top-BSR ASIN (see routes/feed.ts), so scoping the candidates list to
  // that same ASIN shows exactly what that run produces — simplest correct
  // choice without a batched "candidates for these ASINs" endpoint.
  //
  // The route echoes its ASIN pick back in the response; once a run has been
  // kicked off we anchor on THAT asin rather than trusting products[0] to
  // agree — both queries now share a `bsr ASC NULLS LAST, asin ASC` sort,
  // but the response echo is the authoritative link between run and UI.
  const [anchorAsin, setAnchorAsin] = useState<string | null>(null);
  const asin = anchorAsin ?? topAsin ?? '';
  const { data: candidates } = useCandidates(asin);
  const promote = usePromoteCandidate(asin);
  const discover = useDiscoverSupplier(brandCacheId);
  const invalidateRuns = useInvalidateDiscoveryRuns(asin);
  const [pendingRunId, setPendingRunId] = useState<string | null>(null);
  const [runFailed, setRunFailed] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);

  // Poll GET /api/discovery-runs?asin=X (the existing hook, reused here via
  // its queryOptions factory) while a run we just kicked off is in flight;
  // stop once it reaches a terminal status and refresh the candidates list.
  const runsQuery = useQuery({
    ...discoveryRunsQuery(asin),
    enabled: Boolean(asin) && Boolean(pendingRunId),
    refetchInterval: (query) => {
      if (!pendingRunId) return false;
      const match = query.state.data?.find((r) => r.id === pendingRunId);
      if (!match) return 2000;
      return match.status === 'succeeded' || match.status === 'failed' ? false : 2000;
    },
  });

  useEffect(() => {
    if (!pendingRunId) return;
    const match = runsQuery.data?.find((r) => r.id === pendingRunId);
    if (match && (match.status === 'succeeded' || match.status === 'failed')) {
      if (match.status === 'failed') setRunFailed(true);
      invalidateRuns();
      setPendingRunId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runsQuery.data, pendingRunId]);

  const runInFlight = Boolean(pendingRunId);
  const promotedCandidate = candidates?.find((c) => c.promotedSupplierId != null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Supplier</CardTitle>
        <CardDescription>
          Find and promote a supplier contact, then draft an outreach email.
        </CardDescription>
      </CardHeader>

      {!topAsin ? (
        <p className="text-sm text-fg-secondary">No catalog products to search against yet.</p>
      ) : (
        <div className="space-y-4">
          <div>
            <Button
              size="sm"
              variant="secondary"
              disabled={runInFlight || discover.isPending}
              loading={discover.isPending || runInFlight}
              onClick={() => {
                setRunFailed(false);
                discover.mutate(undefined, {
                  onSuccess: (res) => {
                    // Anchor on the server's ASIN pick — see comment above.
                    setAnchorAsin(res.asin);
                    setPendingRunId(res.runId);
                  },
                });
              }}
            >
              Find supplier contact
            </Button>
            {discover.isError && (
              <p role="alert" className="text-xs text-negative mt-2">
                {(discover.error as Error)?.message ?? 'Discovery failed to start'}
              </p>
            )}
            {runFailed && (
              <p role="alert" className="text-xs text-negative mt-2">
                Contact search failed — try again.
              </p>
            )}
          </div>

          {candidates && candidates.length > 0 && (
            <ul className="space-y-2">
              {candidates.map((c) => (
                <CandidateRow
                  key={c.id}
                  candidate={c}
                  onPromote={() => promote.mutate({ id: c.id })}
                  promoting={promote.isPending && promote.variables?.id === c.id}
                />
              ))}
            </ul>
          )}

          {promotedCandidate && (
            <>
              <Button size="sm" onClick={() => setDraftOpen(true)}>
                Draft email
              </Button>
              {/* Stays mounted across open/close (only `open` toggles) so a
                  generated draft persists on re-open instead of triggering a
                  fresh paid LLM call. Keyed on supplierId so a different
                  promoted supplier gets a clean slate. */}
              <DraftEmailDialog
                key={promotedCandidate.promotedSupplierId}
                open={draftOpen}
                supplierId={promotedCandidate.promotedSupplierId!}
                contactEmail={promotedCandidate.contactEmail}
                website={promotedCandidate.website ?? promotedCandidate.sourceUrl}
                claimId={claimId}
                claimStatus={claimStatus}
                onClose={() => setDraftOpen(false)}
              />
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function CandidateRow({
  candidate,
  onPromote,
  promoting,
}: {
  candidate: SupplierCandidate;
  onPromote: () => void;
  promoting: boolean;
}) {
  return (
    <li className="flex items-center justify-between gap-3 border border-border-subtle rounded-sm px-3 py-2">
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{candidate.companyName}</p>
        <p className="text-xs text-fg-tertiary truncate">
          <a
            href={externalUrl(candidate.website ?? candidate.sourceUrl)}
            target="_blank"
            rel="noreferrer"
            className="hover:underline"
          >
            {candidate.website ?? candidate.sourceUrl}
          </a>
          {candidate.contactEmail && (
            <>
              {' · '}
              <a
                href={`mailto:${candidate.contactEmail}`}
                className="text-accent-fg hover:underline"
              >
                {candidate.contactEmail}
              </a>
            </>
          )}
        </p>
      </div>
      {candidate.status === 'promoted' ? (
        <Badge variant="positive">Promoted</Badge>
      ) : (
        <Button size="sm" variant="secondary" onClick={onPromote} loading={promoting}>
          Promote
        </Button>
      )}
    </li>
  );
}

function DraftEmailDialog({
  open,
  supplierId,
  contactEmail,
  website,
  claimId,
  claimStatus,
  onClose,
}: {
  open: boolean;
  supplierId: string;
  contactEmail: string | null;
  website: string;
  claimId: string | null;
  claimStatus: ClaimResponse['status'] | null;
  onClose: () => void;
}) {
  const draft = useDraftOutreach();
  const send = useSendOutreach();
  const advanceClaim = useAdvanceClaim();
  const [copied, setCopied] = useState(false);
  const [composer, dispatchComposer] = useReducer(
    outreachComposerReducer,
    initialOutreachComposerState
  );

  // No mount-effect: generation is an explicit button click. A mount-effect
  // here double-fires under React StrictMode's dev double-mount — two paid
  // LLM calls per dialog open — and would also regenerate on every re-open.

  // Seed the composer from each successful draft/redraft. `draft.data` only
  // gets a new object identity when a mutation resolves, so this does NOT
  // re-fire on dialog close/reopen (the dialog stays mounted — see the
  // keyed-by-supplierId comment below) — only on "Generate draft" / "Draft
  // again".
  useEffect(() => {
    if (draft.data) {
      dispatchComposer({ type: 'seed', subject: draft.data.subject, body: draft.data.body });
    }
  }, [draft.data]);

  async function handleCopy() {
    if (!composer.subject && !composer.body) return;
    try {
      await navigator.clipboard.writeText(`Subject: ${composer.subject}\n\n${composer.body}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // best-effort; clipboard may be denied in non-secure contexts
    }
  }

  function handleDraftAgain() {
    const hasEdits =
      draft.data && (composer.subject !== draft.data.subject || composer.body !== draft.data.body);
    if (hasEdits && !window.confirm('Discard your edits and generate a new draft?')) {
      return;
    }
    draft.mutate({ supplierId, templateKind: 'cold_intro' });
  }

  const canMarkContacted = claimStatus === 'claimed' && Boolean(claimId);
  const markContactedTitle = canMarkContacted
    ? undefined
    : 'Only claims still in "claimed" status can be marked contacted.';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Draft email</DialogTitle>
        </DialogHeader>

        {!draft.data && !draft.isPending && (
          <div>
            <p className="text-sm text-fg-secondary mb-3">
              Generate a cold-intro email for this supplier. Uses your OpenRouter key (one LLM
              call).
            </p>
            <Button
              size="sm"
              onClick={() => draft.mutate({ supplierId, templateKind: 'cold_intro' })}
            >
              Generate draft
            </Button>
          </div>
        )}
        {draft.isPending && <p className="text-sm text-fg-secondary">Drafting…</p>}
        {draft.isError && (
          <p className="text-sm text-negative">
            {(draft.error as Error)?.message ?? 'Draft failed'}
          </p>
        )}
        {draft.data && (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-fg-tertiary mb-1">Send to</p>
                {contactEmail ? (
                  <p className="text-sm">
                    <a href={`mailto:${contactEmail}`} className="text-accent-fg hover:underline">
                      {contactEmail}
                    </a>
                  </p>
                ) : (
                  <p className="text-sm text-fg-secondary">
                    No email found for this supplier — use the contact or wholesale form on{' '}
                    <a
                      href={externalUrl(website)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent-fg hover:underline"
                    >
                      {website}
                    </a>
                    .
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDraftAgain}
                loading={draft.isPending}
                disabled={send.isPending || send.isSuccess}
              >
                Draft again
              </Button>
            </div>
            <OutreachComposer
              supplierId={supplierId}
              state={composer}
              dispatch={dispatchComposer}
              disabled={send.isPending || send.isSuccess}
            />
          </div>
        )}

        {send.isSuccess && (
          <p className="text-sm text-positive">
            Queued — sends from sales@veluxon.com within a minute. The claim moves to Contacted
            automatically once it's delivered.
          </p>
        )}
        {send.isError && (
          <p role="alert" className="text-sm text-negative">
            {(send.error as Error)?.message ?? 'Send failed'}
          </p>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={handleCopy} disabled={!draft.data}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button
            disabled={!draft.data || send.isPending || send.isSuccess}
            loading={send.isPending}
            onClick={() =>
              draft.data &&
              send.mutate({
                supplierId,
                subject: composer.subject,
                body: composer.body,
                ...(claimId ? { claimId } : {}),
              })
            }
          >
            {send.isSuccess ? 'Queued' : 'Send email'}
          </Button>
          <Button
            disabled={!canMarkContacted || advanceClaim.isPending}
            loading={advanceClaim.isPending}
            title={markContactedTitle}
            onClick={() => claimId && advanceClaim.mutate({ id: claimId, status: 'contacted' })}
          >
            Mark as contacted
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
